/**
 * THE DOORMAN'S READ, PUBLISHED (M12, PRD §2.4.2, conversation model §5.2).
 *
 * It is a card IN a conversation, not the end of one. The doorman finishing its
 * questions means "here is my read", never "this is closed": the composer is
 * still below this card, the sender may keep talking, and a turn that brings
 * new evidence re-runs the assessment and replaces what this card says. So
 * there is NO "Knock again" here and no `reknock` block behind it — there is
 * nothing to knock again at, because nothing closed (§10).
 *
 * It renders exactly what the server sends for THIS knock (§2.4.2 rule 2 — no
 * ratchet, no floor, no flattering historical badge) and it renders no number
 * past the work products: no score, no percentile, no rank, no dimensions
 * (§5.4 rule 5). The band is a sentence, not a digit. Free of network state, so every state —
 * cold-start tier, in-recap — is asserted directly. The share affordances (M17,
 * §1.8 loops 1 and 3) are the one exception: each owns its own minting, and
 * neither renders anything until it is asked for.
 */
import type { Standing } from "../../lib/api";
import { sentNote } from "../../lib/conversations";
import { KnockShare, STANDING_SHARE_LABEL } from "../share/knock-share";
import { standingPostText } from "../share/labels";
import { ThreadCard } from "./chat";
import { Receipt } from "./receipt";

export const IMPROVEMENT_HEADING = "What would move you up";

/**
 * What the card is, for anyone not reading it visually — and the handle the e2e
 * tier scopes its §5.4 rule 5 sweep to, now that the resolution concludes a
 * thread rather than replacing it.
 */
export const STANDING_LABEL = "Your standing";

type Props = {
  handle: string;
  standing: Standing;
  /**
   * The knock behind this standing. Present when the screen is the sender's own
   * live knock, which is what a share link is minted against (§1.8 loops 1, 3).
   */
  knockId?: string;
  /** Whose door — for the one sentence a card with no ranking line says instead. */
  doorName?: string | null;
};

export function StandingCard({ handle, standing, knockId, doorName: name = null }: Props) {
  const doorName = name?.trim() || `@${handle}`;
  return (
    <ThreadCard label={STANDING_LABEL}>
      {/* The band as a plain statement — the whole resolution, no number behind it.
          Absent at a door whose rules treat a person in danger (R-0925-1 (3)):
          the server sends no ranking line there, and no morning-brief line. */}
      <div role="status">
        {standing.band_label ? (
          <p className="text-[1.15rem] leading-snug text-text">{standing.band_label}</p>
        ) : (
          // No ranking line to show — a door whose rules treat a person in
          // danger — and a card with nothing on it read as a broken one. The
          // fact is what is left: the read went to the owner, theirs to act on.
          <p data-sent-note className="text-[15px] leading-relaxed text-text">
            {sentNote(doorName)}
          </p>
        )}
        {standing.recap_note && (
          <p className="mt-2 text-[15px] leading-relaxed text-faint">{standing.recap_note}</p>
        )}
      </div>

      {/* Loop 1 (§1.8): the standing flex, offered under the band it is about.
          Auto-generated above the 90th percentile and nowhere else, so the
          affordance appears only where the server will mint one — and anything
          it declines anyway disappears without comment (see `KnockShare`). */}
      {knockId && standing.band === "TOP_5" && standing.band_label && (
        <KnockShare
          kind="standing"
          knockId={knockId}
          label={STANDING_SHARE_LABEL}
          postText={standingPostText(standing.band_label, handle)}
          imageAlt={`Standing card: ${standing.band_label}`}
        />
      )}

      {/* What would move you up — the improvement, in an amber-edged quiet card.
          Only where the server says it can be honestly met (ruling R-0925-1
          (3)): never under an exclusion, a closed door, a strict hold, a
          changed story or a guard stop, where it read as the refusal restated
          or as coaching for the next attempt. */}
      {standing.improvement_shown && standing.improvement && (
        <section className="mt-6 rounded-r-lg border-l-2 border-accent/70 bg-surface/40 px-4 py-3">
          <h2 className="font-mono text-[0.8rem] uppercase tracking-[0.14em] text-faint">
            {IMPROVEMENT_HEADING}
          </h2>
          <p className="mt-2 text-[15px] leading-relaxed text-text-soft">{standing.improvement}</p>
        </section>
      )}

      {/* What the doorman did, in work products and no figure (FR-3.5). */}
      <Receipt receipt={standing.receipt} knockId={knockId} />

      {/* Under a rule: the disclaimer, quiet, and nothing above it — and only
          under a reasonable or strong card, which the server decides by sending
          it or not (R-0925-1): "a high standing" under a weak one was about a
          standing the knocker has not got. The
          re-knock offer is gone with the thing it offered — the conversation
          this card sits in is still open, and the composer below is the way on
          (§6.1). The disclaimer stays: it is what stops a read from being taken
          for a promise. */}
      {standing.disclaimer && (
        <div className="mt-6 border-t border-border pt-5">
          <p className="text-[13px] leading-relaxed text-faint">{standing.disclaimer}</p>
        </div>
      )}
    </ThreadCard>
  );
}
